import { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import DatePicker from "react-datepicker";
import { DateTimeInput } from "../../UI/DateTimeInput";
import { Spinner } from "../../UI/Spinner";
import { clearOrder, getShowtimes } from "../../../redux/slices";
import { compareDiffInHours } from "../../../utils/dateHelpers";
import { STATUS } from "../../../utils/constants";
import startOfDay from "date-fns/startOfDay";
import { useTranslation } from "react-i18next";

const ShowtimeDateModal = ({ openStatus, closeFunc }) => {
  const { t } = useTranslation(["Home", "Common"]);
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.showtimes);
  const [selectedTime, setSelectedTime] = useState(new Date().toISOString());
  const [dateError, setDateError] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [showFutureConfirm, setShowFutureConfirm] = useState(false);
  const isFetching = status === STATUS.PENDING;

  const isFutureDate = (dateStr) => {
    return startOfDay(new Date(dateStr)) > startOfDay(new Date());
  };

  const fetchShowtimes = () => {
    if (isFetching) return;

    dispatch(getShowtimes({ startDate: selectedTime }))
      .unwrap()
      .then((data) => {
        setDateError(false);
        setFetchError("");
        closeFunc();
        // The response echoes back dateTo as the END of the fetched day.
        // Storing it as-is meant a subsequent submit sent
        // startDate === endDate, so the request came back empty and the fetch
        // looked like it had silently failed. Normalise to the start of the
        // day that was just fetched.
        setSelectedTime(
          startOfDay(
            new Date(data?.paramFilter?.dateTo || selectedTime)
          ).toISOString()
        );
        dispatch(clearOrder());
      })
      .catch((err) => {
        // Without this the rejection was swallowed: the modal stayed open with
        // no feedback at all, so a failed fetch was indistinguishable from one
        // that never happened.
        setFetchError(
          typeof err === "string" && err
            ? err
            : t(
                "ticket.date.fetchError",
                "Unable to fetch showtimes. Please try again."
              )
        );
      });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const checkDiff = compareDiffInHours(new Date(selectedTime), new Date());

    if (checkDiff >= -24) {
      if (isFutureDate(selectedTime)) {
        setShowFutureConfirm(true);
      } else {
        fetchShowtimes();
      }
    } else {
      setDateError(true);
    }
  };

  const handleConfirm = () => {
    setShowFutureConfirm(false);
    fetchShowtimes();
  };

  const handleCancelConfirm = () => {
    setShowFutureConfirm(false);
  };

  return (
    <>
      <Transition appear show={openStatus} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex mt-[10%]  justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform  rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 text-center mb-4"
                  >
                    {t("ticket.date.title")}
                  </Dialog.Title>
                  <form onSubmit={onSubmit}>
                    {dateError && (
                      <p className="text-red-500 text-center">
                        {t("ticket.date.error")}
                      </p>
                    )}
                    {fetchError && (
                      <p className="text-red-500 text-center">{fetchError}</p>
                    )}
                    <div className="px-[50px]">
                      <DatePicker
                        placeholderText={t("ticket.date.placeholder")}
                        selected={new Date(selectedTime)}
                        onChange={(value) => {
                          setSelectedTime(startOfDay(value).toISOString());
                          setDateError(false);
                          setFetchError("");
                        }}
                        customInput={
                          <DateTimeInput
                            className="bg-primary"
                            dateFormat={"EEE, MMM dd, yyyy"}
                          />
                        }
                      />
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button
                        type="submit"
                        disabled={isFetching}
                        className="inline-flex items-center mr-2 border justify-center rounded-md border-blue-900 bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      >
                        <span>{t("ticket.date.submit")}</span>
                        <Spinner
                          className="!w-3 !h-3 !mr-0 ml-2"
                          status={status}
                        />
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        onClick={() => {
                          closeFunc();
                          setDateError(false);
                          setFetchError("");
                        }}
                      >
                        {t("Common:button.cancel")}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Future Date Confirmation Modal */}
      <Transition appear show={showFutureConfirm} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-20"
          onClose={handleCancelConfirm}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex mt-[10%] justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 text-center mb-4"
                  >
                    {t("ticket.date.futureConfirmTitle", "Confirm Future Date")}
                  </Dialog.Title>
                  <p className="text-sm text-gray-500 text-center mb-6">
                    {t(
                      "ticket.date.futureConfirmMessage",
                      "You are about to fetch showtimes for a future date. Do you want to proceed?"
                    )}
                  </p>
                  <div className="flex justify-center gap-2">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-blue-900 bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={handleConfirm}
                    >
                      {t("Common:button.confirm", "Confirm")}
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={handleCancelConfirm}
                    >
                      {t("Common:button.cancel")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
export default ShowtimeDateModal;
